import React, { useState } from "react";
import { Upload, File, AlertCircle, CheckCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";

interface FileUploaderProps {
  onFileSelect: (file: File, fileName: string) => void;
}

const FileUploader: React.FC<FileUploaderProps> = ({ onFileSelect }) => {
  const { toast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      validateAndProcessFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      validateAndProcessFile(file);
    }
  };

  const validateAndProcessFile = (file: File) => {
    // Vérifier l'extension du fichier
    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Format de fichier non supporté",
        description: "Veuillez télécharger un fichier CSV",
        variant: "destructive"
      });
      return;
    }

    // Limiter la taille du fichier (ex: 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Fichier trop volumineux",
        description: "La taille du fichier ne doit pas dépasser 5MB",
        variant: "destructive"
      });
      return;
    }

    setSelectedFile(file);
    onFileSelect(file, file.name);
    
    toast({
      title: "Fichier sélectionné",
      description: `"${file.name}" a été chargé avec succès`,
    });
  };

  return (
    <div className="w-full">
      <div 
        className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center transition-colors
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} 
          ${selectedFile ? 'bg-green-50 border-green-300' : 'hover:bg-gray-50'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {selectedFile ? (
          <div className="text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
            <h4 className="font-medium mb-1">{selectedFile.name}</h4>
            <p className="text-sm text-gray-500 mb-3">
              {(selectedFile.size / 1024).toFixed(2)} KB
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setSelectedFile(null)}
            >
              Changer de fichier
            </Button>
          </div>
        ) : (
          <>
            <Upload className="h-12 w-12 text-gray-400 mb-3" />
            <h4 className="text-lg font-medium mb-2">Déposez votre fichier CSV ici</h4>
            <p className="text-sm text-gray-500 mb-4 text-center">
              ou cliquez pour sélectionner un fichier depuis votre ordinateur
            </p>
            <Button variant="secondary" asChild>
              <label htmlFor="file-upload" className="cursor-pointer">
                Parcourir
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  accept=".csv"
                  onChange={handleFileSelect}
                />
              </label>
            </Button>
          </>
        )}
      </div>

      {selectedFile && (
        <div className="mt-4 flex items-center p-3 bg-blue-50 rounded-lg">
          <File className="h-5 w-5 text-blue-500 mr-2" />
          <span className="text-sm">
            <span className="font-medium">{selectedFile.name}</span> est prêt à être importé
          </span>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
